import { Divider, Modal, ModalBody, ModalContent, ModalHeader, Tab, Tabs } from '@ds';
import { ChevronUp } from '@icons';
import { useState } from 'react';
import { getClinicalItemTimestamp } from '@features/components/ClinicalData/sections/utils';
import { OffOCSRegularForm, OffOCSSequentialForm } from '@lp/features/forms/OcsStatusForms';
import {
  getCaseStatusName,
  getClinicalRecordByType,
  getColorsByOrganType,
  isRandomizationScs,
  ORGAN_ICON,
} from '@/utils/helpers';
import { Case, CaseStatus, ClinicalItemType, OrganType } from '@/api/apis/case';
import { DeviceImage } from '../DeviceImage';
import { getOrganTypeLabel } from './utils';

type OffOcsModalProps = {
  onOpenChange: () => void;
  caseData: Case;
};

export const OffOcsModal = ({ caseData, onOpenChange }: OffOcsModalProps) => {
  const { caseId, organType, clinicalData, txCenterDetails, randomization } = caseData;
  const isScs = isRandomizationScs(randomization);
  const [selectedTab, setSelectedTab] = useState('regular');
  const OrganIcon = ORGAN_ICON[organType];
  const colors = getColorsByOrganType(organType);
  const ocsStartClinicalRecord = getClinicalRecordByType(clinicalData, ClinicalItemType.OrganOnOCS);
  const scsStartClinicalRecord = getClinicalRecordByType(clinicalData, ClinicalItemType.OrganOnSCS);
  const onDeviceRecord = isScs ? scsStartClinicalRecord : ocsStartClinicalRecord;
  const deviceStartTime = getClinicalItemTimestamp(onDeviceRecord);

  return (
    <Modal
      isOpen
      onOpenChange={onOpenChange}
      size="xl"
      classNames={{
        wrapper: 'flex-wrap z-[420]',
        header: 'pb-0',
        footer: 'flex flex-col gap-6 pt-0 pb-6',
        closeButton: 'text-hl',
        backdrop: 'z-[419]',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-2">
          <h3 className="text-hl text-base-10">
            {getOrganTypeLabel(organType, isScs)} {getCaseStatusName(CaseStatus.OrganOffOCS, randomization?.result)}
          </h3>
          <Divider />
        </ModalHeader>
        <ModalBody className="py-6">
          <div className="flex flex-col gap-6">
            <div className="w-full justify-center flex items-center mb-4 gap-3">
              <DeviceImage isScs={isScs} ocsColor={colors.text50} />
              <ChevronUp className={colors.text50} />
              <OrganIcon className={`${colors.text50} w-[64px] h-auto`} />
            </div>
            {organType === OrganType.Lungs && !isScs ? (
              <Tabs
                onSelectionChange={(t) => setSelectedTab(t as string)}
                selectedKey={selectedTab}
                aria-label="Off OCS type"
                classNames={{
                  tabList: 'bg-base-50 w-[350px] p-0 gap-0',
                  tab: 'text-s-sb',
                  tabContent: 'text-base-5 group-data-[selected]:text-white',
                  cursor: 'bg-base-5 text-white',
                  panel: 'flex flex-col flex-1 py-0',
                  base: 'w-full justify-center',
                }}
                radius="full"
              >
                <Tab key="regular" title="Double Lung Flush">
                  <OffOCSRegularForm
                    caseId={caseId}
                    randomization={randomization}
                    ocsStartTimestamp={deviceStartTime?.timestamp}
                    txCenter={txCenterDetails}
                    organType={organType}
                    onOpenChange={onOpenChange}
                  />
                </Tab>
                <Tab key="sequential" title="Sequential Lung Flush">
                  <OffOCSSequentialForm
                    caseId={caseId}
                    randomization={randomization}
                    ocsStartTimestamp={deviceStartTime?.timestamp}
                    txCenter={txCenterDetails}
                    onOpenChange={onOpenChange}
                  />
                </Tab>
              </Tabs>
            ) : (
              <OffOCSRegularForm
                caseId={caseId}
                randomization={randomization}
                ocsStartTimestamp={deviceStartTime?.timestamp}
                txCenter={txCenterDetails}
                organType={organType}
                onOpenChange={onOpenChange}
              />
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
